import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import { categoryLabel } from './CategoryBadge';
import Button from '../ui/Button';
import { convertCandidateVehicleType } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';

export function targetVehicleCategory(candidateCategory, categories) {
  if (!candidateCategory) return null;
  const targetVehicleType = candidateCategory.vehicle_type === 'taxi' ? 'bike' : 'taxi';
  return (categories || []).find(
    (c) =>
      c.id !== candidateCategory.id &&
      c.vehicle_type === targetVehicleType &&
      c.location === candidateCategory.location &&
      c.license_type === candidateCategory.license_type &&
      c.is_active
  );
}

/**
 * Confirms switching a candidate between the taxi/bike version of their pipeline. Unlike
 * "Change category" this doesn't reset progress: any step already completed/approved/passed/
 * failed/skipped carries over onto the matching step of the new category (matched by the
 * underlying step, not position), and steps unique to one pipeline are dropped/added as needed.
 * A carried-over step can end up missing documents/fields the new category's version of it
 * requires — those show up flagged on the workspace afterwards for staff to fill in.
 */
export default function ConvertVehicleTypeModal({ open, onClose, candidate, categories, onConverted }) {
  const queryClient = useQueryClient();
  const target = targetVehicleCategory(candidate?.category, categories);

  const mutation = useMutation({
    mutationFn: () => convertCandidateVehicleType(candidate.id),
    onSuccess: (updated) => {
      pushToast({ type: 'success', message: `Converted to ${target?.name || 'the new'} workflow.` });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      onConverted?.(updated);
      onClose();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not convert.' }),
  });

  if (!candidate) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert vehicle type"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!target}>
            Convert
          </Button>
        </>
      }
    >
      {target ? (
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            Move <strong>{candidate.full_name}</strong> from <strong>{categoryLabel(candidate.category)}</strong> to{' '}
            <strong>{categoryLabel(target)}</strong>.
          </p>
          <p>
            Steps already completed, approved, passed, failed or skipped carry over as-is. Steps unique to the new
            workflow start pending; steps only in the old workflow are dropped. A carried-over step missing
            documents or fields the new workflow requires will be flagged for review.
          </p>
        </div>
      ) : (
        <p className="text-sm text-red-600">No active matching workflow category found for the opposite vehicle type.</p>
      )}
    </Modal>
  );
}
